/**
 * CoverageSparkline — Feb 2026.
 *
 * Tiny 4-quarter sparkline chip rendering the last four SEC-reported
 * coverage ratios (from `sec_coverage.quarters[].ratio`). Sits INSIDE
 * the Coverage Radar ticker chip so a user can eyeball the coverage
 * trajectory without opening the Stock Modal.
 *
 * Rendering rules:
 *   • Dot color mirrors the coverage tier at that quarter (rose <1.0×,
 *     amber 1.0–1.5×, emerald ≥1.5×) so red-dots-trending-up reads as
 *     "climbing out of trouble" and green-dots-trending-down reads as
 *     "healthy but slipping".
 *   • Connecting line color = tone of the enclosing card (rose = cut,
 *     emerald = recovery) so the sparkline always reinforces WHY the
 *     ticker is on that radar.
 *   • Pure inline SVG so mounts are effectively free (no chart lib).
 *
 * Null-renders when fewer than 2 data points are present.
 */
export default function CoverageSparkline({ history, tone = 'emerald' }) {
  if (!Array.isArray(history) || history.length < 2) return null;
  const ratios = history.map((h) => Number(h.ratio)).filter((n) => Number.isFinite(n));
  if (ratios.length < 2) return null;

  // Compact viewport: 56 × 18 fits comfortably inside the two-line
  // Coverage Radar chip without pushing the trend row out.
  const W = 56;
  const H = 18;
  const PAD = 2;

  // Y-axis anchors 1.0× (the coverage floor) in the middle so a
  // ticker crossing above/below the line is instantly visible.
  const minR = Math.min(0.7, ...ratios);
  const maxR = Math.max(1.6, ...ratios);
  const span = Math.max(0.001, maxR - minR);
  const yFor = (r) => H - PAD - ((r - minR) / span) * (H - 2 * PAD);
  const xFor = (i) => PAD + (i / (ratios.length - 1)) * (W - 2 * PAD);

  const points = ratios.map((r, i) => `${xFor(i)},${yFor(r)}`).join(' ');
  const lineStroke = tone === 'rose'
    ? 'rgb(244 63 94)'      // rose-500
    : 'rgb(16 185 129)';    // emerald-500
  const oneXLine = yFor(1.0);

  const dotColor = (r) => {
    if (r < 1.0) return 'rgb(244 63 94)';        // rose-500
    if (r < 1.5) return 'rgb(245 158 11)';       // amber-500
    return 'rgb(16 185 129)';                     // emerald-500
  };

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="shrink-0"
      aria-label={`Coverage sparkline: ${ratios.map((r) => r.toFixed(2) + '×').join(', ')}`}
      data-testid="coverage-sparkline"
    >
      {/* 1.0× reference line — dashed so it reads as a boundary,
          not a data series. */}
      <line
        x1={PAD} x2={W - PAD}
        y1={oneXLine} y2={oneXLine}
        stroke="currentColor"
        strokeWidth="0.5"
        strokeDasharray="1.5 1.5"
        opacity="0.35"
      />
      <polyline
        fill="none"
        stroke={lineStroke}
        strokeWidth="1.25"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
      />
      {ratios.map((r, i) => {
        // Feb 2026 — Native SVG `<title>` gives a first-class browser
        // tooltip on hover across desktop + assistive tech, without
        // adding React state (the sparkline lives inside a chip
        // button so any pointer/tap already navigates to the Stock
        // Modal — a portal-style hover card would just fight that).
        const endLabel = history[i]?.end
          ? (() => {
              const d = new Date(history[i].end);
              return Number.isNaN(d.getTime())
                ? String(history[i].end)
                : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            })()
          : `Q${i + 1}`;
        return (
          <circle
            key={i}
            cx={xFor(i)}
            cy={yFor(r)}
            r="1.5"
            fill={dotColor(r)}
          >
            <title>{`${endLabel} · ${r.toFixed(2)}× coverage`}</title>
          </circle>
        );
      })}
    </svg>
  );
}
